import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX, MouseEvent, ReactNode } from 'react'
import { parseAsNativeArrayOf, parseAsString, useQueryState } from 'nuqs'

import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { Temporal } from '@js-temporal/polyfill'
import { clsx } from 'clsx'

import {
  AlertDialog,
  Button,
  CalendarIcon,
  DropdownMenu,
  EmptyState,
  FileTextIcon,
  Flex,
  IconButton,
  Notice,
  PageHeader,
  TagIcon,
  pageHeaderStyles,
  PageLayout,
  PencilIcon,
  PlusIcon,
  Slot,
  Text,
  TrashIcon,
  ListIcon,
  toast,
  useMediaQuery,
} from '@buffer-mono/popcorn'

import { Calendar } from '~publish/components/Calendar'
import { CalendarSlotButton } from '~publish/components/Calendar/CalendarSlotButton'
import { CalendarUndatedDraftCard } from '~publish/components/Calendar/CalendarUndatedDraftCard'
import {
  CalendarUndatedDraftDragOverlay,
  CalendarUndatedDraftDragSource,
} from '~publish/components/Calendar/CalendarUndatedDraftDragSource'
import {
  CalendarUndatedDraftMiniCard,
  CalendarUndatedDraftMiniTrigger,
  CalendarUndatedDraftsList,
  CalendarUndatedDraftsPanel,
  useUndatedDraftsPanelKeyboard,
} from '~publish/components/Calendar/CalendarUndatedDraftsPanel'
import calendarWorkspaceStyles from '~publish/components/Calendar/CalendarWorkspace.module.css'
import {
  UndatedDraftsToggle,
  useUndatedDraftsToggleFocusReturn,
} from '~publish/components/Calendar/UndatedDraftsToggle'
import { useCalendar } from '~publish/components/Calendar/hooks/useCalendar'
import { useCalendarMobileMode } from '~publish/components/Calendar/hooks/useCalendarMobileMode'
import type {
  CalendarItem,
  RenderItemFunction,
} from '~publish/components/Calendar/types'
import {
  ContentItemCardView,
  ContentItemDetailsCardHeaderView,
  ContentItemDetailsCardView,
  getDraftPreviewLabel,
} from '~publish/components/ContentItemCard'
import {
  GuestMediaPreview,
  GuestMediaThumbnail,
} from '~publish/components/GuestMedia'
import { NewPostHeaderActions } from '~publish/components/NewPostHeaderActions'
import { FilterButton } from '~publish/components/FilterButton'
import {
  FilterByTagView,
  UNTAGGED_VALUE,
} from '~publish/components/FilterByTag'
import { MoreActionsDropdown } from '~publish/components/PostCard/PostCardActions/MoreActionsDropdown'
import { PostCardContent } from '~publish/components/PostCardContent'
import { PostingTimeline } from '~publish/components/PostingTimeline'
import { ViewToggle } from '~publish/components/PostListOrCalendarViewToggle'
import type { ViewToggleOption } from '~publish/components/PostListOrCalendarViewToggle'
import {
  PostEmptyStateByTab,
  PostTabs,
  PostTabsResponsive,
} from '~publish/components/PostTabs'
import type { PostTab } from '~publish/components/PostTabs'
import { ProductPageTitle } from '~publish/components/ProductPageTitle'
import { buildPreviewCalendarSlots } from '~publish/helpers/buildPreviewCalendarSlots'
import { draftActionLabels } from '~publish/helpers/posts/draftActionLabels'
import { postSatisfiesTagFilter } from '~publish/helpers/posts/isPostSatisfyingFilters'
import {
  CURRENT_TIME_ZONE,
  formatDateTime,
} from '~publish/helpers/dateFormatters'
import type { GuestDraft, GuestDraftStore } from '~publish/helpers/guestDrafts'
import { getGuestNetworkIdForService } from '~publish/helpers/guestNetworks'
import type { GuestNetworkId } from '~publish/helpers/guestNetworks'
import type { GuestTag } from '~publish/helpers/guestTags'
import {
  getGuestHourCycle,
  getGuestWeekStartsOn,
} from '~publish/helpers/guestTimeFormat'
import { endOfDay, startOfHour, startOfWeek } from '~publish/helpers/temporal'
import type { WeekAnchor } from '~publish/helpers/temporal'
import type { GuestMediaAccessController } from '~publish/hooks/useGuestMediaAccess'
import {
  MAX_EXPANDED_QUEUE_SLOTS,
  MAX_VISIBLE_QUEUE_SLOTS,
  ValueLedQueueSlots,
  buildPreviewQueueSlots,
} from '~publish/pages/AllChannels/ValueLedQueueEmptyState'
import type { ValueLedQueueSlot } from '~publish/pages/AllChannels/ValueLedQueueEmptyState/buildValueLedQueueSlots'
import { CalendarControls } from '~publish/pages/Calendar/CalendarHeader/CalendarControls'
import { MobileCalendarDateLabel } from '~publish/pages/Calendar/CalendarHeader/MobileCalendarDateLabel'
import { ContentItemCalendarItemView } from '~publish/pages/Calendar/ContentItemCalendarItem'
import { DraftsTimeline } from '~publish/pages/AllChannels/PostList/DraftsTimeline'
import { ContentItemPopover } from '~publish/pages/Calendar/ContentItemPopover'
import {
  getContentItemDropTimestamp,
  getContentItemSuccessMessage,
  getNewTimestamp,
} from '~publish/pages/Calendar/hooks/useHandleDragEnd'

import {
  getGuestUndatedDraftDragData,
  getGuestUndatedDraftDragLabel,
  isGuestUndatedDraftDragData,
} from './guestDraftDrag'
import {
  GuestDraftNetworkIdentity,
  GuestDraftNetworks,
  getGuestDraftNetworkNames,
} from './GuestDraftNetworks'
import styles from './PublicSchedulePage.module.css'
import { PublicTimezoneSignup } from './PublicTimezoneSignup'

const DRAFTS_PANEL_ID = 'public-calendar-drafts'
const MAX_COMPACT_NETWORKS = 4
const DRAFTS_TOGGLE_TOOLTIP = 'Drafts saved in this browser without a date'
const CALENDAR_HIDDEN_MEDIA_QUERY = '(width <= 1024px)'
const PUBLIC_WEEK_ANCHOR: WeekAnchor = 'previous-day'
const PUBLIC_VIEW_OPTIONS: ViewToggleOption[] = [
  { key: 'queue', title: 'List', icon: <ListIcon /> },
  { key: 'calendar', title: 'Calendar', icon: <CalendarIcon /> },
]

type PublicScheduleView = 'calendar' | 'queue'

function getGuestTagId(tag: GuestTag): string {
  return tag.name.toLowerCase()
}

function toHourKey(timestamp: number): number {
  return startOfHour(
    Temporal.Instant.fromEpochMilliseconds(timestamp).toZonedDateTimeISO(
      CURRENT_TIME_ZONE,
    ),
  ).epochMilliseconds
}

function formatGuestDraftTime(timestamp: number): string {
  return formatDateTime(new Date(timestamp).toISOString(), {
    timeZone: CURRENT_TIME_ZONE,
    timeFormat: getGuestHourCycle() === 24 ? '24' : '12',
    includeTimeZone: false,
  })
}

function renderCompactNetworks(draft: GuestDraft): JSX.Element | undefined {
  if (!draft.networks?.length) return undefined

  return (
    <GuestDraftNetworks networks={draft.networks} max={MAX_COMPACT_NETWORKS} />
  )
}

function getGuestDraftPreviewLabel(draft: GuestDraft): string {
  const label = getDraftPreviewLabel(draft.text)
  const networkNames = getGuestDraftNetworkNames(draft.networks)
  return networkNames.length > 0
    ? `${label} for ${networkNames.join(', ')}`
    : label
}

function renderCompactMedia(
  draft: GuestDraft,
  mediaAccess: GuestMediaAccessController,
): JSX.Element | undefined {
  const [firstMedia] = draft.media
  if (!firstMedia) return undefined

  return <GuestMediaThumbnail media={firstMedia} access={mediaAccess} />
}

function GuestDraftSource({ draft }: { draft: GuestDraft }): JSX.Element {
  return (
    <Flex gap="xs" align="center">
      <GuestDraftNetworks networks={draft.networks} />
      <Text color="subtle" size="sm">
        Saved in this browser
      </Text>
    </Flex>
  )
}

function GuestDraftActions({
  draft,
  onEdit,
  onDelete,
}: {
  draft: GuestDraft
  onEdit: (id: string) => void
  onDelete: (draft: GuestDraft) => void
}): JSX.Element {
  const { editLabel, moreActionsLabel } = draftActionLabels({
    text: draft.text,
    createdAtLabel: formatGuestDraftTime(draft.createdAt),
    assets: [],
  })

  return (
    <>
      <IconButton
        variant="secondary"
        label={editLabel}
        tooltip="Edit"
        onClick={(): void => onEdit(draft.id)}
      >
        <PencilIcon />
      </IconButton>
      <MoreActionsDropdown label={moreActionsLabel}>
        <MoreActionsDropdown.Group>
          <DropdownMenu.Item
            variant="critical"
            onSelect={(): void => onDelete(draft)}
          >
            <TrashIcon />
            Delete
          </DropdownMenu.Item>
        </MoreActionsDropdown.Group>
      </MoreActionsDropdown>
    </>
  )
}

function GuestDraftDetailsCard({
  draft,
  mediaAccess,
  onEdit,
  onDelete,
}: {
  draft: GuestDraft
  mediaAccess: GuestMediaAccessController
  onEdit: (id: string) => void
  onDelete: (draft: GuestDraft) => void
}): JSX.Element {
  return (
    <ContentItemDetailsCardView
      header={
        <ContentItemDetailsCardHeaderView
          dateLabel={
            draft.scheduledAt === undefined
              ? 'No date'
              : formatGuestDraftTime(draft.scheduledAt)
          }
        />
      }
      content={
        <>
          <GuestDraftNetworkIdentity networks={draft.networks} />
          <PostCardContent text={draft.text} annotations={[]} />
        </>
      }
      media={
        draft.media.length > 0 ? (
          <GuestMediaPreview
            media={draft.media}
            access={mediaAccess}
            className={styles.detailsMedia}
          />
        ) : undefined
      }
      author={
        <Text color="subtle" size="sm">
          Saved in this browser
        </Text>
      }
      actions={
        <GuestDraftActions draft={draft} onEdit={onEdit} onDelete={onDelete} />
      }
    />
  )
}

function GuestDraftCard({
  draft,
  mediaAccess,
  onEdit,
  onDelete,
}: {
  draft: GuestDraft
  mediaAccess: GuestMediaAccessController
  onEdit: (id: string) => void
  onDelete: (draft: GuestDraft) => void
}): JSX.Element {
  return (
    <ContentItemCardView
      content={<PostCardContent text={draft.text} annotations={[]} />}
      media={
        <GuestMediaPreview
          media={draft.media}
          access={mediaAccess}
          className={styles.mediaPreview}
        />
      }
      meta={<GuestDraftSource draft={draft} />}
      actions={
        <GuestDraftActions draft={draft} onEdit={onEdit} onDelete={onDelete} />
      }
    />
  )
}

function GuestDraftMiniCard({
  draft,
  mediaAccess,
  isShelfOpen,
  onEdit,
  onDelete,
}: {
  draft: GuestDraft
  mediaAccess: GuestMediaAccessController
  isShelfOpen: boolean
  onEdit: (id: string) => void
  onDelete: (draft: GuestDraft) => void
}): JSX.Element {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  useEffect(() => {
    if (!isShelfOpen) setIsPreviewOpen(false)
  }, [isShelfOpen])

  return (
    <ContentItemPopover
      isOpen={isPreviewOpen}
      onOpenChange={setIsPreviewOpen}
      side="left"
      preview={
        <GuestDraftDetailsCard
          draft={draft}
          mediaAccess={mediaAccess}
          onEdit={onEdit}
          onDelete={(selected): void => {
            setIsPreviewOpen(false)
            onDelete(selected)
          }}
        />
      }
    >
      <CalendarUndatedDraftMiniTrigger
        aria-label={getGuestDraftPreviewLabel(draft)}
      >
        <CalendarUndatedDraftCard
          text={draft.text}
          networks={renderCompactNetworks(draft)}
          media={renderCompactMedia(draft, mediaAccess)}
        />
      </CalendarUndatedDraftMiniTrigger>
    </ContentItemPopover>
  )
}

function GuestDraftCalendarItem({
  draft,
  mediaAccess,
  condensed,
  onEdit,
  onDelete,
}: {
  draft: GuestDraft
  mediaAccess: GuestMediaAccessController
  condensed: boolean
  onEdit: (id: string) => void
  onDelete: (draft: GuestDraft) => void
}): JSX.Element {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  return (
    <ContentItemCalendarItemView
      text={draft.text}
      label={getGuestDraftPreviewLabel(draft)}
      networks={renderCompactNetworks(draft)}
      media={renderCompactMedia(draft, mediaAccess)}
      condensed={condensed}
      isOpen={isPreviewOpen}
      onOpenChange={setIsPreviewOpen}
      preview={
        <GuestDraftDetailsCard
          draft={draft}
          mediaAccess={mediaAccess}
          onEdit={onEdit}
          onDelete={(selected): void => {
            setIsPreviewOpen(false)
            onDelete(selected)
          }}
        />
      }
    />
  )
}

function GuestDraftsList({
  drafts,
  mediaAccess,
  onCreate,
  onEdit,
  onDelete,
}: {
  drafts: readonly GuestDraft[]
  mediaAccess: GuestMediaAccessController
  onCreate: () => void
  onEdit: (id: string) => void
  onDelete: (draft: GuestDraft) => void
}): JSX.Element {
  if (drafts.length === 0) {
    return (
      <PostEmptyStateByTab tab="drafts">
        <Button size="large" onClick={onCreate}>
          <PlusIcon /> New Post
        </Button>
      </PostEmptyStateByTab>
    )
  }

  const undated = drafts.filter((draft) => draft.scheduledAt === undefined)
  const dated = drafts
    .flatMap((draft) =>
      draft.scheduledAt === undefined
        ? []
        : [{ draft, date: new Date(draft.scheduledAt).toISOString() }],
    )
    .toSorted((left, right) => left.date.localeCompare(right.date))
  const renderCard = (draft: GuestDraft): JSX.Element => (
    <GuestDraftCard
      draft={draft}
      mediaAccess={mediaAccess}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  )

  return (
    <DraftsTimeline
      role="feed"
      aria-label="Draft posts"
      undated={undated}
      dated={dated}
      getKey={(item): string => ('draft' in item ? item.draft.id : item.id)}
      renderUndated={(draft): JSX.Element => (
        <PostingTimeline.Entry>
          <PostingTimeline.TimeLabel status="draft" />
          <PostingTimeline.Content>{renderCard(draft)}</PostingTimeline.Content>
        </PostingTimeline.Entry>
      )}
      renderDated={({ draft, date }): JSX.Element => (
        <PostingTimeline.Entry>
          <PostingTimeline.TimeLabel status="draft" date={date} />
          <PostingTimeline.Content>{renderCard(draft)}</PostingTimeline.Content>
        </PostingTimeline.Entry>
      )}
    />
  )
}

export function PublicSchedulePage({
  drafts: allDrafts,
  savedDraftId,
  store,
  onCreate,
  mediaAccess,
  onEdit,
}: {
  drafts: readonly GuestDraft[]
  savedDraftId?: string
  store: GuestDraftStore
  onCreate: (scheduledAt?: number, network?: GuestNetworkId) => void
  mediaAccess: GuestMediaAccessController
  onEdit: (id: string) => void
}): JSX.Element {
  const [draftToDelete, setDraftToDelete] = useState<GuestDraft>()
  const [deleteError, setDeleteError] = useState<'changed' | 'failed'>()
  const [tabParam, setTabParam] = useQueryState(
    'tab',
    parseAsString.withOptions({ history: 'push' }),
  )
  const view: PublicScheduleView =
    tabParam === 'queue' || tabParam === 'drafts' ? 'queue' : 'calendar'
  const listTab: PostTab = tabParam === 'drafts' ? 'drafts' : 'queue'
  const [tagsParam, setTagsParam] = useQueryState(
    'tags[]',
    parseAsNativeArrayOf(parseAsString).withOptions({ history: 'push' }),
  )
  const tags = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; color: string }>()
    for (const tag of allDrafts.flatMap((draft) => draft.tags ?? [])) {
      const id = getGuestTagId(tag)
      if (!byId.has(id)) byId.set(id, { id, ...tag })
    }
    return Array.from(byId.values()).toSorted((a, b) =>
      a.name.localeCompare(b.name),
    )
  }, [allDrafts])
  const selectedTagIds = useMemo(
    () =>
      tagsParam.filter(
        (id) => id === UNTAGGED_VALUE || tags.some((tag) => tag.id === id),
      ),
    [tagsParam, tags],
  )
  const drafts = useMemo(
    () =>
      allDrafts.filter((draft) =>
        postSatisfiesTagFilter(
          (draft.tags ?? []).map(getGuestTagId),
          selectedTagIds,
        ),
      ),
    [allDrafts, selectedTagIds],
  )
  const isWideViewport = useMediaQuery('(width >= 1024px)')
  const isCalendarMobile = useCalendarMobileMode()
  const isCalendarHidden = useMediaQuery(CALENDAR_HIDDEN_MEDIA_QUERY)
  const undatedDrafts = useMemo(
    () => drafts.filter((draft) => draft.scheduledAt === undefined),
    [drafts],
  )
  const [isDraftsOpen, setIsDraftsOpen] = useState(
    () => isWideViewport && undatedDrafts.length > 0,
  )
  const [openedForDraftId, setOpenedForDraftId] = useState<string>()
  const [isDraftsOpenedByUser, setIsDraftsOpenedByUser] = useState(false)
  const draftsPanelRef = useRef<HTMLElement>(null)
  const draftsCloseButtonRef = useRef<HTMLButtonElement>(null)
  const closeDrafts = useCallback((): void => setIsDraftsOpen(false), [])

  useEffect(() => {
    if (
      !savedDraftId ||
      savedDraftId === openedForDraftId ||
      !isWideViewport ||
      !undatedDrafts.some((draft) => draft.id === savedDraftId)
    ) {
      return
    }
    setOpenedForDraftId(savedDraftId)
    setIsDraftsOpen(true)
    setIsDraftsOpenedByUser(false)
  }, [isWideViewport, openedForDraftId, savedDraftId, undatedDrafts])

  const weekStartsOn = getGuestWeekStartsOn()
  const is24HourFormat = getGuestHourCycle() === 24
  const { wideRef: wideDraftsToggleRef, narrowRef: narrowDraftsToggleRef } =
    useUndatedDraftsToggleFocusReturn(isDraftsOpen)
  useUndatedDraftsPanelKeyboard({
    isOpen: isDraftsOpen,
    panelRef: draftsPanelRef,
    closeButtonRef: draftsCloseButtonRef,
    onClose: closeDrafts,
    focusOnOpen: isDraftsOpenedByUser,
    closeOnPageEscape: true,
  })
  const { viewMode, startDate, endDate } = useCalendar({
    timezone: CURRENT_TIME_ZONE,
    weekStartsOn,
    weekAnchor: PUBLIC_WEEK_ANCHOR,
  })
  const draftHours = useMemo(
    () =>
      new Set(
        allDrafts.flatMap((draft) =>
          draft.scheduledAt === undefined ? [] : [toHourKey(draft.scheduledAt)],
        ),
      ),
    [allDrafts],
  )
  const slots = useMemo(() => {
    const rangeStart = startDate.toInstant().epochMilliseconds
    const rangeEnd = endOfDay(endDate).toInstant().epochMilliseconds
    return buildPreviewCalendarSlots({
      onboardingServices: null,
      timezone: CURRENT_TIME_ZONE,
      weekStartsOn,
      startTimestamp: startOfWeek(startDate, {
        weekStartsOn,
      }).toInstant().epochMilliseconds,
      endDate: endOfDay(
        startOfWeek(endDate, { weekStartsOn }).add({
          days: 6,
        }),
      )
        .toInstant()
        .toString(),
    }).filter((slot) => {
      const slotTime = new Date(slot.date).getTime()
      return (
        slotTime >= rangeStart &&
        slotTime <= rangeEnd &&
        !draftHours.has(toHourKey(slotTime))
      )
    })
  }, [draftHours, endDate, startDate, weekStartsOn])
  const items = useMemo<CalendarItem[]>(
    () => [
      ...slots.map<CalendarItem>((slot) => ({
        id: slot.id,
        timestamp: new Date(slot.date).getTime(),
        type: 'slot',
      })),
      ...drafts.flatMap<CalendarItem>((draft) =>
        draft.scheduledAt === undefined
          ? []
          : [
              {
                id: draft.id,
                timestamp: draft.scheduledAt,
                type: 'contentItem',
                draggable: true,
                droppable: false,
              },
            ],
      ),
    ],
    [drafts, slots],
  )
  const slotScrollTime = useMemo(() => {
    const rangeStart = startDate.toInstant().epochMilliseconds
    const rangeEnd = endOfDay(endDate).toInstant().epochMilliseconds
    const visibleItems = items.filter(
      (item) => item.timestamp >= rangeStart && item.timestamp <= rangeEnd,
    )
    if (visibleItems.some((item) => item.type !== 'slot')) return undefined

    const minuteOfDay = (timestamp: number): number => {
      const time =
        Temporal.Instant.fromEpochMilliseconds(timestamp).toZonedDateTimeISO(
          CURRENT_TIME_ZONE,
        )
      return time.hour * 60 + time.minute
    }
    return visibleItems
      .map((item) => item.timestamp)
      .toSorted(
        (left, right) => minuteOfDay(left) - minuteOfDay(right) || left - right,
      )[0]
  }, [endDate, items, startDate])
  const renderItem = useCallback<RenderItemFunction>(
    ({ id, timestamp, type }) => {
      if (type === 'contentItem') {
        const draft = drafts.find((candidate) => candidate.id === id)
        if (!draft) return null

        return (
          <GuestDraftCalendarItem
            draft={draft}
            mediaAccess={mediaAccess}
            condensed={viewMode === 'month'}
            onEdit={onEdit}
            onDelete={(selected): void => {
              setDraftToDelete(selected)
              setDeleteError(undefined)
            }}
          />
        )
      }
      if (type !== 'slot') return null

      const slot = slots.find((candidate) => candidate.id === id)
      if (!slot) return null

      return (
        <CalendarSlotButton
          variant={
            isCalendarMobile && (viewMode === 'month' || viewMode === 'week')
              ? viewMode
              : 'default'
          }
          timestamp={timestamp}
          timezone={CURRENT_TIME_ZONE}
          channelId={slot.channelId}
          channelName={slot.channelName}
          channelService={slot.channelService}
          is24HourFormat={is24HourFormat}
          onClick={(event): void => {
            event.preventDefault()
            onCreate(
              timestamp,
              getGuestNetworkIdForService(slot.channelService),
            )
          }}
        />
      )
    },
    [
      drafts,
      is24HourFormat,
      isCalendarMobile,
      mediaAccess,
      onCreate,
      onEdit,
      slots,
      viewMode,
    ],
  )
  const addItemProps = useMemo(() => ({ onClick: onCreate }), [onCreate])
  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent): void => {
      if (over?.data.current?.type !== 'hour') return

      const undatedDrag = isGuestUndatedDraftDragData(active.data.current)
        ? active.data.current
        : undefined
      if (!undatedDrag && active.data.current?.type !== 'contentItem') return
      if (
        !undatedDrag &&
        active.data.current?.timestamp === over.data.current?.timestamp
      ) {
        return
      }

      const draftId = undatedDrag?.draftId ?? active.id.toString()
      const draft = drafts.find((candidate) => candidate.id === draftId)
      if (!draft) return

      const targetDate =
        !undatedDrag && draft.scheduledAt !== undefined
          ? getNewTimestamp(
              new Date(draft.scheduledAt).toISOString(),
              over.data.current?.timestamp,
              viewMode === 'month',
              CURRENT_TIME_ZONE,
            )
          : getContentItemDropTimestamp(over.data.current)
      if (!targetDate) return

      const result = store.save(
        draft.id,
        {
          text: draft.text,
          media: draft.media,
          scheduledAt: Date.parse(targetDate),
        },
        draft.updatedAt,
      )
      if (result.status === 'saved' || result.status === 'unchanged') {
        toast.success(
          getContentItemSuccessMessage(
            targetDate,
            CURRENT_TIME_ZONE,
            is24HourFormat,
            undatedDrag ? 'scheduled' : 'rescheduled',
          ),
        )
        return
      }
      toast.error(
        result.status === 'conflict'
          ? 'This draft changed in another tab. Check it before you move it.'
          : 'We couldn’t move this draft. Try again.',
      )
    },
    [drafts, is24HourFormat, store, viewMode],
  )
  const renderDragOverlay = useCallback(
    (active: DragStartEvent['active']): ReactNode => {
      if (!isGuestUndatedDraftDragData(active.data.current)) return null
      const { draftId } = active.data.current
      const draft = drafts.find((candidate) => candidate.id === draftId)
      if (!draft) return null

      return (
        <CalendarUndatedDraftDragOverlay>
          <GuestDraftCalendarItem
            draft={draft}
            mediaAccess={mediaAccess}
            condensed
            onEdit={onEdit}
            onDelete={(): void => undefined}
          />
        </CalendarUndatedDraftDragOverlay>
      )
    },
    [drafts, mediaAccess, onEdit],
  )
  const { previewQueueSlots, expandedQueueSlots } = useMemo(() => {
    const queueSlots = buildPreviewQueueSlots({
      onboardingServices: null,
      timezone: CURRENT_TIME_ZONE,
      limit: MAX_EXPANDED_QUEUE_SLOTS + draftHours.size,
    })
      .filter((slot) => !draftHours.has(toHourKey(Date.parse(slot.date))))
      .slice(0, MAX_EXPANDED_QUEUE_SLOTS)
    return {
      previewQueueSlots: queueSlots.slice(0, MAX_VISIBLE_QUEUE_SLOTS),
      expandedQueueSlots: queueSlots,
    }
  }, [draftHours])

  const closeDeleteDialog = (): void => {
    setDraftToDelete(undefined)
    setDeleteError(undefined)
  }

  const handleDelete = (event: MouseEvent<HTMLButtonElement>): void => {
    if (!draftToDelete) return

    const result = store.delete(draftToDelete.id, draftToDelete.updatedAt)
    if (result.status === 'deleted' || result.status === 'not-found') {
      closeDeleteDialog()
      return
    }

    event.preventDefault()
    if (result.status === 'conflict') {
      setDraftToDelete(result.draft)
      setDeleteError('changed')
      return
    }

    setDeleteError('failed')
  }

  const toggleAction = isDraftsOpen ? 'Hide' : 'Show'
  const toggleText = `${toggleAction} Drafts`
  const toggleLabel =
    undatedDrafts.length > 0
      ? `${toggleText} (${undatedDrafts.length})`
      : toggleText
  const toggleDrafts = (): void => {
    setIsDraftsOpenedByUser(!isDraftsOpen)
    setIsDraftsOpen(!isDraftsOpen)
  }
  const headerRowSize = view === 'queue' ? undefined : 'full'
  const tabs = [
    { id: 'queue' as const, count: undefined },
    { id: 'drafts' as const, count: drafts.length },
  ]

  const selectListTab = (tab: PostTab): void => {
    if (tab === 'drafts' || tab === 'queue') void setTabParam(tab)
  }

  const viewToggle = (
    <ViewToggle
      options={PUBLIC_VIEW_OPTIONS}
      value={view}
      onChange={(nextView): void => {
        if (nextView === 'queue') {
          void setTabParam(listTab)
        } else if (nextView === 'calendar') {
          void setTabParam(null)
        }
      }}
    />
  )

  const selectTags = (tagIds: string[]): void => {
    void setTagsParam(tagIds)
  }
  const tagFilter = (
    <FilterByTagView tags={tags} value={selectedTagIds} onSelect={selectTags} />
  )
  const narrowTagFilter = (
    <FilterByTagView tags={tags} value={selectedTagIds} onSelect={selectTags}>
      <FilterButton
        icon={<TagIcon />}
        variant="tertiary"
        size="medium"
        aria-label="Filter by tags"
        hideChevron
      >
        {null}
      </FilterButton>
    </FilterByTagView>
  )

  const selectDraftForDelete = (draft: GuestDraft): void => {
    setDraftToDelete(draft)
    setDeleteError(undefined)
  }

  return (
    <PostTabs asChild value={listTab} onChange={selectListTab}>
      <PageLayout className={styles.page}>
        <PageHeader separator={view === 'queue'}>
          <PageHeader.Row size={headerRowSize}>
            <ProductPageTitle
              icon={<CalendarIcon size={24} />}
              title="Publish"
            />
            <PageHeader.Actions>
              <div className={pageHeaderStyles.hideOnNarrow}>{viewToggle}</div>
              <NewPostHeaderActions
                wrapTrigger={(trigger): JSX.Element => (
                  <Slot.Root onClick={(): void => onCreate()}>
                    {trigger}
                  </Slot.Root>
                )}
              />
            </PageHeader.Actions>
          </PageHeader.Row>
          {view === 'calendar' ? (
            <PageHeader.Row size="full">
              <div className={pageHeaderStyles.hideOnNarrow}>
                <CalendarControls
                  weekStartsOn={weekStartsOn}
                  weekAnchor={PUBLIC_WEEK_ANCHOR}
                />
              </div>
              <MobileCalendarDateLabel
                className={pageHeaderStyles.hideOnWide}
              />
              <PageHeader.Actions>
                <div className={pageHeaderStyles.hideOnNarrow}>{tagFilter}</div>
                <div className={pageHeaderStyles.hideOnNarrow}>
                  <UndatedDraftsToggle
                    ref={wideDraftsToggleRef}
                    variant="wide"
                    isOpen={isDraftsOpen}
                    label={toggleLabel}
                    tooltip={DRAFTS_TOGGLE_TOOLTIP}
                    controlsId={DRAFTS_PANEL_ID}
                    count={undatedDrafts.length}
                    onToggle={toggleDrafts}
                  >
                    {toggleText}
                  </UndatedDraftsToggle>
                </div>
                <div className={pageHeaderStyles.hideOnWide}>
                  {narrowTagFilter}
                </div>
                <div className={pageHeaderStyles.hideOnWide}>{viewToggle}</div>
                <UndatedDraftsToggle
                  ref={narrowDraftsToggleRef}
                  variant="narrow"
                  isOpen={isDraftsOpen}
                  label={toggleLabel}
                  tooltip={DRAFTS_TOGGLE_TOOLTIP}
                  controlsId={DRAFTS_PANEL_ID}
                  onToggle={toggleDrafts}
                  className={pageHeaderStyles.hideOnWide}
                />
                <div className={pageHeaderStyles.hideOnNarrow}>
                  <PublicTimezoneSignup store={store} />
                </div>
              </PageHeader.Actions>
            </PageHeader.Row>
          ) : (
            <PageHeader.Row size={headerRowSize}>
              <PostTabsResponsive
                tabs={tabs}
                value={listTab}
                onChange={selectListTab}
              />
              <PageHeader.Actions>
                <div className={pageHeaderStyles.hideOnNarrow}>{tagFilter}</div>
                <div className={pageHeaderStyles.hideOnNarrow}>
                  <PublicTimezoneSignup store={store} />
                </div>
                <div className={pageHeaderStyles.hideOnWide}>
                  {narrowTagFilter}
                </div>
                <div className={pageHeaderStyles.hideOnWide}>{viewToggle}</div>
              </PageHeader.Actions>
            </PageHeader.Row>
          )}
        </PageHeader>

        {view === 'calendar' ? (
          <PageLayout.Container
            size="full"
            className={styles.calendarContainer}
          >
            <div
              className={clsx(calendarWorkspaceStyles.workspace, {
                [calendarWorkspaceStyles.expanded]: isDraftsOpen,
              })}
            >
              <Calendar
                className={calendarWorkspaceStyles.pane}
                timezone={CURRENT_TIME_ZONE}
                weekStartsOn={weekStartsOn}
                weekAnchor={PUBLIC_WEEK_ANCHOR}
                is24HourFormat={is24HourFormat}
                readOnly={false}
                loading={false}
                items={items}
                scrollMonthToToday
                scrollToTime={slotScrollTime}
                renderItem={renderItem}
                addItemProps={addItemProps}
                onDragStart={(): void => undefined}
                onDragEnd={handleDragEnd}
                renderDragOverlay={renderDragOverlay}
              >
                <CalendarUndatedDraftsPanel
                  ref={draftsPanelRef}
                  id={DRAFTS_PANEL_ID}
                  closeButtonRef={draftsCloseButtonRef}
                  heading="Drafts"
                  description="Drafts saved in this browser without a date."
                  isEmpty={undatedDrafts.length === 0}
                  closeLabel="Close Drafts"
                  onClose={closeDrafts}
                  hidden={!isDraftsOpen}
                >
                  {undatedDrafts.length === 0 ? (
                    <EmptyState size="small">
                      <EmptyState.Icon>
                        <FileTextIcon />
                      </EmptyState.Icon>
                      <div id={`${DRAFTS_PANEL_ID}-heading`}>
                        <EmptyState.Heading as="h2">
                          No Undated Drafts
                        </EmptyState.Heading>
                      </div>
                      <EmptyState.Description
                        id={`${DRAFTS_PANEL_ID}-description`}
                      >
                        Drafts saved in this browser without a date will appear
                        here.
                      </EmptyState.Description>
                    </EmptyState>
                  ) : (
                    <CalendarUndatedDraftsList
                      role="feed"
                      aria-label="Draft posts"
                    >
                      {undatedDrafts.map((draft) => (
                        <CalendarUndatedDraftMiniCard key={draft.id}>
                          <CalendarUndatedDraftDragSource
                            id={`guest-undated:${draft.id}`}
                            data={getGuestUndatedDraftDragData(draft)}
                            disabled={isCalendarHidden}
                            dragLabel={getGuestUndatedDraftDragLabel(draft)}
                          >
                            <GuestDraftMiniCard
                              draft={draft}
                              mediaAccess={mediaAccess}
                              isShelfOpen={isDraftsOpen}
                              onEdit={onEdit}
                              onDelete={selectDraftForDelete}
                            />
                          </CalendarUndatedDraftDragSource>
                        </CalendarUndatedDraftMiniCard>
                      ))}
                    </CalendarUndatedDraftsList>
                  )}
                </CalendarUndatedDraftsPanel>
              </Calendar>
            </div>
          </PageLayout.Container>
        ) : (
          <PageLayout.Container size="narrow" className={styles.queueContainer}>
            <PostTabs.Panel value="queue">
              <ValueLedQueueSlots
                previewSlots={previewQueueSlots}
                expandedSlots={expandedQueueSlots}
                onSlotClick={(
                  slot: ValueLedQueueSlot,
                  event: MouseEvent<HTMLButtonElement>,
                ): void => {
                  event.preventDefault()
                  onCreate(
                    new Date(slot.date).getTime(),
                    getGuestNetworkIdForService(slot.channelService),
                  )
                }}
              />
            </PostTabs.Panel>
            <PostTabs.Panel value="drafts">
              <GuestDraftsList
                drafts={drafts}
                mediaAccess={mediaAccess}
                onCreate={(): void => onCreate()}
                onEdit={onEdit}
                onDelete={selectDraftForDelete}
              />
            </PostTabs.Panel>
          </PageLayout.Container>
        )}

        <AlertDialog
          open={Boolean(draftToDelete)}
          onOpenChange={(open): void => {
            if (!open) closeDeleteDialog()
          }}
        >
          <AlertDialog.Content>
            <AlertDialog.Title>Delete draft?</AlertDialog.Title>
            <AlertDialog.Description>
              This draft will be deleted from this browser.
            </AlertDialog.Description>
            {deleteError ? (
              <AlertDialog.Body>
                <Notice variant="error">
                  <Notice.Text>
                    {deleteError === 'changed'
                      ? 'This draft changed in another tab. Check it before you delete it.'
                      : 'We couldn’t delete this draft. Try again.'}
                  </Notice.Text>
                </Notice>
              </AlertDialog.Body>
            ) : null}
            <AlertDialog.Actions>
              <AlertDialog.Cancel>
                <Button variant="tertiary">Cancel</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button variant="critical" onClick={handleDelete}>
                  Delete
                </Button>
              </AlertDialog.Action>
            </AlertDialog.Actions>
          </AlertDialog.Content>
        </AlertDialog>
      </PageLayout>
    </PostTabs>
  )
}
